import { useState } from "react";
import { DEFAULT_AI_MODEL_ID, PLAN_PRICES_USD, modelTokenCost } from "@winche/shared";
import { Callout } from "@winche/ui";
import { Block, Metric, MetricGrid, Panel, SectionHeader, Segmented } from "../components/ui";
import { DataTable } from "../components/table";
import { num, pct, usd, usdUnit } from "../lib/format";

// ─── Cost model ────────────────────────────────────────────────────────────────
// Per-token retail prices from MODEL_COST_MAP (@winche/shared), the map the
// backend estimates and budgets with (ux-admin-07). A model off the map is
// "price unknown", never priced as another model. Always retail: the free
// tier is a subsidy that can be revoked.

/** Per-token USD price of `modelId`, or null when MODEL_COST_MAP does not list it. */
function tokenPriceOf(modelId: string): { input: number; output: number } | null {
  const cost = modelTokenCost(modelId);
  return cost ? { input: cost.promptPerToken, output: cost.completionPerToken } : null;
}

/** Simulates infrastructure and MRR when the AI price is unknown; AI figures are then masked. */
const NO_PRICE = { input: 0, output: 0 };

// ─── Token estimates per operation ────────────────────────────────────────────
// Derived from actual maxTokens caps and measured prompt sizes in the codebase.
// Source files:
//   evaluate_job       → job-evaluator.ts      maxTokens:4096  (typically uses ~800 out)
//   prescreen_jobs     → job-prescreener.ts     maxTokens:2048  (batch of ~10 jobs)
//   tailor_cv          → cv-generator.ts        maxTokens:4096  (large CV JSON)
//   refine_cv          → cv-refine.ts           maxTokens:4096  (same as tailor)
//   review_cv          → cv-reviewer.ts         maxTokens:1024  (vision, short output)
//   ats_keywords       → ats-keywords-extract.ts maxTokens:512  (compact JD → keywords)
//   jd_compress        → jd-for-tailoring.ts    maxTokens:2048  (JD → structured)
//   cover_letter       → cover-letter-generator.ts maxTokens:650
//   interview_prep     → interview-prep-generator.ts maxTokens:4000
//   negotiation_guide  → negotiation-generator.ts  maxTokens:2000
//   reply_draft        → reply-draft-generator.ts  maxTokens:500
//   follow_up          → follow-up-generator.ts    maxTokens:600
//   email_classify     → email-classifier.ts       no maxTokens (default 1024)
//   search_expand      → search-expander.ts        maxTokens:300
const OPS = {
  // Per-job operations (run for every evaluated job)
  evalJob:       { input: 2200, output: 850  }, // profile block (cached) + JD + structured output
  prescreenBatch:{ input: 2000, output: 400  }, // batch of ~10 jobs, JSON array output
  // Per-CV generation pipeline (tailor + optional refine + review + ats + jd_compress)
  tailorCv:      { input: 3000, output: 3000 }, // full profile + JD + structured CV JSON
  refineCv:      { input: 3000, output: 3000 }, // same context with issue list
  reviewCv:      { input: 800,  output: 300  }, // vision model, short analysis
  atsKeywords:   { input: 600,  output: 200  }, // JD → 10-15 keywords
  jdCompress:    { input: 2500, output: 800  }, // full JD → structured summary
  // Per-action operations
  coverLetter:   { input: 1400, output: 600  }, // expanded JD context (was 2000 after our change)
  interviewPrep: { input: 2800, output: 2000 }, // profile + JD + company notes
  negotiation:   { input: 2000, output: 1500 }, // profile + offer context
  replyDraft:    { input: 1200, output: 450  }, // thread context + tone instructions
  followUp:      { input: 1000, output: 500  }, // application context + timing
  emailClassify: { input: 600,  output: 150  }, // email body → classification JSON
  searchExpand:  { input: 400,  output: 250  }, // keywords → expanded query list
};

// Plan prices: PLAN_PRICES_USD, the source admin-billing.ts computes MRR from.
const PRICE_SEARCH = PLAN_PRICES_USD.search;
const PRICE_RADAR  = PLAN_PRICES_USD.radar;

const OR_FREE = { rpm: 20, rpd: 500 };
const NEW_JOBS_PER_DAY = 800;

function tc(input: number, output: number, price: { input: number; output: number }) {
  return input * price.input + output * price.output;
}

// Full AI cost per user per month, broken down by user type.
// Assumptions are documented inline and match plan-limits.ts exactly.
function userAiCost(
  type: "free" | "search" | "radar",
  price: { input: number; output: number }
): number {
  if (type === "free") {
    // plan-limits free: 1 scan/day cap → ~4 scans/month realistically used
    // evalsPerScan: 50 (plan limit). prescreenBatch runs once per scan (10 jobs/batch → 5 batches).
    // applicationsPerDay: 3 → ~12/month. Each CV gen: tailor + jd_compress + ats_keywords + review (no refine assumed).
    // cover_letter: ~1/month. interview_prep: ~1/month (prepsPerWeek: 3 → generous).
    // reply_draft: ~1/month. email_classify: ~2/month.
    const scans = 4;
    const evals = scans * 50;
    const prescreenBatches = scans * 5; // 50 evals / 10 per batch
    const cvs = 12; // 3 apps/day × ~4 active days
    const letters = 1;
    const preps = 1;
    const drafts = 1;
    const classifies = 2;
    return (
      evals          * tc(OPS.evalJob.input,       OPS.evalJob.output,       price) +
      prescreenBatches * tc(OPS.prescreenBatch.input, OPS.prescreenBatch.output, price) +
      cvs            * tc(OPS.tailorCv.input,      OPS.tailorCv.output,      price) +
      cvs            * tc(OPS.jdCompress.input,    OPS.jdCompress.output,    price) +
      cvs            * tc(OPS.atsKeywords.input,   OPS.atsKeywords.output,   price) +
      cvs            * tc(OPS.reviewCv.input,      OPS.reviewCv.output,      price) +
      letters        * tc(OPS.coverLetter.input,   OPS.coverLetter.output,   price) +
      preps          * tc(OPS.interviewPrep.input, OPS.interviewPrep.output, price) +
      drafts         * tc(OPS.replyDraft.input,    OPS.replyDraft.output,    price) +
      classifies     * tc(OPS.emailClassify.input, OPS.emailClassify.output, price)
    );
  }

  if (type === "search") {
    // plan-limits pro:search: 2 scans/day → ~33 usable scans/month (some days off)
    // evalsPerScan: 300 (plan limit). applicationsPerDay: unlimited → ~40/month realistically.
    // CV gen pipeline: tailor + refine (20% chance) + jd_compress + ats + review.
    // cover_letter: ~8/month. interview_prep: ~3/month. negotiation: ~1/month.
    // reply_draft: ~5/month. email_classify: ~10/month. The send form is built
    // from the profile without an LLM (buildBasicFormData), so it costs nothing.
    const scans = 33;
    const evals = scans * 200; // ~200 avg (not always hitting 300 cap)
    const prescreenBatches = scans * 20; // 200 evals / 10 per batch
    const cvs = 40;
    const cvRefines = cvs * 0.2; // 20% need a refine pass
    const letters = 8;
    const preps = 3;
    const negotiations = 1;
    const drafts = 5;
    const classifies = 10;
    return (
      evals          * tc(OPS.evalJob.input,       OPS.evalJob.output,       price) +
      prescreenBatches * tc(OPS.prescreenBatch.input, OPS.prescreenBatch.output, price) +
      cvs            * tc(OPS.tailorCv.input,      OPS.tailorCv.output,      price) +
      cvRefines      * tc(OPS.refineCv.input,      OPS.refineCv.output,      price) +
      cvs            * tc(OPS.jdCompress.input,    OPS.jdCompress.output,    price) +
      cvs            * tc(OPS.atsKeywords.input,   OPS.atsKeywords.output,   price) +
      cvs            * tc(OPS.reviewCv.input,      OPS.reviewCv.output,      price) +
      letters        * tc(OPS.coverLetter.input,   OPS.coverLetter.output,   price) +
      preps          * tc(OPS.interviewPrep.input, OPS.interviewPrep.output, price) +
      negotiations   * tc(OPS.negotiation.input,   OPS.negotiation.output,   price) +
      drafts         * tc(OPS.replyDraft.input,    OPS.replyDraft.output,    price) +
      classifies     * tc(OPS.emailClassify.input, OPS.emailClassify.output, price)
    );
  }

  // radar: plan-limits pro:radar: 4 scans/month (automatic, weekly).
  // evalsPerScan: 50. applicationsPerDay: 2 → ~8/month.
  // prepsPerMonth: 5. cover_letter: ~2/month. reply_draft: ~2/month. email_classify: ~4/month.
  const scans = 4;
  const evals = scans * 50;
  const prescreenBatches = scans * 5;
  const cvs = 8;
  const letters = 2;
  const preps = 2;
  const drafts = 2;
  const classifies = 4;
  return (
    evals          * tc(OPS.evalJob.input,       OPS.evalJob.output,       price) +
    prescreenBatches * tc(OPS.prescreenBatch.input, OPS.prescreenBatch.output, price) +
    cvs            * tc(OPS.tailorCv.input,      OPS.tailorCv.output,      price) +
    cvs            * tc(OPS.jdCompress.input,    OPS.jdCompress.output,    price) +
    cvs            * tc(OPS.atsKeywords.input,   OPS.atsKeywords.output,   price) +
    cvs            * tc(OPS.reviewCv.input,      OPS.reviewCv.output,      price) +
    letters        * tc(OPS.coverLetter.input,   OPS.coverLetter.output,   price) +
    preps          * tc(OPS.interviewPrep.input, OPS.interviewPrep.output, price) +
    drafts         * tc(OPS.replyDraft.input,    OPS.replyDraft.output,    price) +
    classifies     * tc(OPS.emailClassify.input, OPS.emailClassify.output, price)
  );
}

interface SimResult {
  mrr: number;
  aiCostFree: number; aiCostSearch: number; aiCostRadar: number; aiTotal: number;
  inactiveAiSaved: number;
  fsWritesCost: number; fsReadsCost: number; cfCost: number; storageCost: number; emailCost: number;
  infraTotal: number; totalCost: number;
  grossProfit: number; grossMarginPct: number;
  conversionPct: number;
  peakRpm: number; dailyReqs: number;
  orFreeOk: boolean; orRpdOk: boolean;
  activeUsers: number;
  aiCostPerFreeUser: number; aiCostPerSearchUser: number; aiCostPerRadarUser: number;
}

function simulate(free: number, search: number, radar: number, inactivePct: number, price: { input: number; output: number }): SimResult {
  const total = free + search + radar;
  const inactiveFree = Math.round(free * inactivePct / 100);
  const activeFree   = free - inactiveFree;
  const activeUsers  = activeFree + search + radar;

  const costPerFree   = userAiCost("free",   price);
  const costPerSearch = userAiCost("search", price);
  const costPerRadar  = userAiCost("radar",  price);

  const aiCostFree   = activeFree * costPerFree;
  const aiCostSearch = search     * costPerSearch;
  const aiCostRadar  = radar      * costPerRadar;
  const aiTotal      = aiCostFree + aiCostSearch + aiCostRadar;
  const inactiveAiSaved = inactiveFree * costPerFree;

  // MRR — exact prices matching Lemon Squeezy variant amounts
  const mrr = search * PRICE_SEARCH + radar * PRICE_RADAR;

  // Firestore writes — fan-out only for active users
  const fanOut    = NEW_JOBS_PER_DAY * activeUsers * 30;
  const actionW   = activeFree*(4*50+12*3) + search*(33*200+40*3) + radar*(4*50+8*3);
  const schedW    = (100 + total*2) * 30;
  const billableW = Math.max(0, fanOut + actionW + schedW - 600_000);
  const fsWritesCost = (billableW / 100_000) * 0.18;

  // Firestore reads
  const scanR  = activeFree*4*100 + search*33*300 + radar*4*100;
  const miscR  = total*5*30 + 7500*30;
  const billableR = Math.max(0, scanR + miscR - 1_500_000);
  const fsReadsCost = (billableR / 100_000) * 0.06;

  // Cloud Functions
  const invocations  = total*20*2 + total*5 + total*30*3 + 120;
  const billableInv  = Math.max(0, invocations - 2_000_000);
  const cfCost       = (billableInv / 1_000_000) * 0.40;

  // Storage (~15 MB per CV PDF × avg 20 CVs per user, generous estimate)
  const storageGB    = (total * 15) / 1024;
  const storageCost  = Math.max(0, storageGB - 5) * 0.20;

  // Email (Mailgun: 5K/mo free, $0.50/1K after)
  const emails       = total * 0.3 * 20;
  const emailCost    = Math.max(0, emails - 5000) / 1000 * 0.50;

  const infraTotal   = fsWritesCost + fsReadsCost + cfCost + storageCost + emailCost;
  const totalCost    = infraTotal + aiTotal;
  const grossProfit  = mrr - totalCost;
  const grossMarginPct = mrr > 0 ? (grossProfit / mrr) * 100 : 0;
  const conversionPct  = total > 0 ? ((search + radar) / total) * 100 : 0;

  // OpenRouter load — concurrent scanners assumed 10% of total at peak
  const concurrentScanners = Math.ceil(activeUsers * 0.1);
  const avgEvalsPerScan = search > 0 ? 200 : 50;
  const peakRpm  = concurrentScanners * (avgEvalsPerScan / 2);
  const dailyReqs = (activeFree*4*50 + search*33*200 + radar*4*50 + activeUsers*3) / 30;

  return {
    mrr, aiCostFree, aiCostSearch, aiCostRadar, aiTotal, inactiveAiSaved,
    fsWritesCost, fsReadsCost, cfCost, storageCost, emailCost,
    infraTotal, totalCost, grossProfit, grossMarginPct, conversionPct,
    peakRpm, dailyReqs, activeUsers,
    orFreeOk: peakRpm <= OR_FREE.rpm,
    orRpdOk:  dailyReqs <= OR_FREE.rpd,
    aiCostPerFreeUser: costPerFree,
    aiCostPerSearchUser: costPerSearch,
    aiCostPerRadarUser: costPerRadar,
  };
}

// ─── Per-operation breakdown (same counts as userAiCost) ─────────────────────

type OpKey = keyof typeof OPS;
const BREAKDOWN: { op: string; key: OpKey; free: number; search: number; radar: number }[] = [
  { op: "evaluate_job", key: "evalJob", free: 4 * 50, search: 33 * 200, radar: 4 * 50 },
  { op: "prescreen_jobs", key: "prescreenBatch", free: 4 * 5, search: 33 * 20, radar: 4 * 5 },
  { op: "tailor_cv", key: "tailorCv", free: 12, search: 40, radar: 8 },
  { op: "refine_cv", key: "refineCv", free: 0, search: 8, radar: 0 },
  { op: "jd_compress", key: "jdCompress", free: 12, search: 40, radar: 8 },
  { op: "ats_keywords", key: "atsKeywords", free: 12, search: 40, radar: 8 },
  { op: "review_cv", key: "reviewCv", free: 12, search: 40, radar: 8 },
  { op: "cover_letter", key: "coverLetter", free: 1, search: 8, radar: 2 },
  { op: "interview_prep", key: "interviewPrep", free: 1, search: 3, radar: 2 },
  { op: "negotiation_guide", key: "negotiation", free: 0, search: 1, radar: 0 },
  { op: "reply_draft", key: "replyDraft", free: 1, search: 5, radar: 2 },
  { op: "email_classify", key: "emailClassify", free: 2, search: 10, radar: 4 },
];

// ─── Controls ─────────────────────────────────────────────────────────────────

function Slider({
  id,
  label,
  value,
  display,
  min,
  max,
  step,
  minLabel,
  maxLabel,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  minLabel: string;
  maxLabel: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-label text-muted-foreground">
          {label}
        </label>
        <span className="text-sm font-semibold tabular-nums text-foreground">{display}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer appearance-none"
      />
      <div className="mt-0.5 flex justify-between text-caption text-muted-foreground">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

// Industry benchmarks: freemium job-search SaaS (Profitwell, Teal, Rezi, Kickresume)
// Conversion: avg 3-5% freemium → paid; job-search tools run 5-8% due to high intent
// Monthly retention free tier: 10-15% bad, 20-30% typical, 35-50% strong PMF
const SCENARIOS = {
  bad: { label: "Bad", total: 100, searchPct: 2, radarPct: 1, retentionPct: 10 },
  normal: { label: "Typical", total: 500, searchPct: 5, radarPct: 2, retentionPct: 25 },
  good: { label: "Good", total: 1500, searchPct: 8, radarPct: 3.5, retentionPct: 40 },
} as const;
type ScenarioKey = keyof typeof SCENARIOS | "custom";

// ─── Main component ────────────────────────────────────────────────────────────

export function SimulatorSection({ activeModelId }: { activeModelId?: string }) {
  const [total, setTotal] = useState(230);
  const [searchPct, setSearchPct] = useState(5);
  const [radarPct, setRadarPct] = useState(2.5);
  const [retentionPct, setRetentionPct] = useState(25);
  const [scenario, setScenario] = useState<ScenarioKey>("custom");

  const custom = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setScenario("custom");
  };
  const applyScenario = (key: ScenarioKey) => {
    setScenario(key);
    if (key === "custom") return;
    const s = SCENARIOS[key];
    setTotal(s.total);
    setSearchPct(s.searchPct);
    setRadarPct(s.radarPct);
    setRetentionPct(s.retentionPct);
  };

  const search = Math.round((total * searchPct) / 100);
  const radar = Math.round((total * radarPct) / 100);
  const free = Math.max(0, total - search - radar);
  const inactivePct = 100 - retentionPct;

  const resolvedModelId = activeModelId ?? DEFAULT_AI_MODEL_ID;
  const knownPrice = tokenPriceOf(resolvedModelId);
  const priceKnown = knownPrice != null;
  const tokenPrice = knownPrice ?? NO_PRICE;
  const sim = simulate(free, search, radar, inactivePct, tokenPrice);
  /** An AI amount, or "price unknown" when the active model is off MODEL_COST_MAP. */
  const ai = (format: (v: number) => string, v: number) => (priceKnown ? format(v) : "price unknown");
  const inactiveFree = Math.round((free * inactivePct) / 100);
  const activeFree = free - inactiveFree;
  const orOk = sim.orFreeOk && sim.orRpdOk;
  const losing = sim.mrr > 0 && sim.totalCost > sim.mrr;

  const costRows: { label: string; value: number | null; note: string; warn?: boolean; ai?: boolean }[] = [
    { label: "AI — free users (active)", value: sim.aiCostFree, ai: true, note: `${num(activeFree)} active × ${ai(usdUnit, sim.aiCostPerFreeUser)} per user` },
    { label: "AI — free users (inactive)", value: null, note: `${num(inactiveFree)} inactive users skipped, ${ai(usd, sim.inactiveAiSaved)} saved` },
    { label: "AI — PRO Search", value: sim.aiCostSearch, ai: true, note: `${num(search)} users × ${ai(usdUnit, sim.aiCostPerSearchUser)} per user` },
    { label: "AI — PRO Radar", value: sim.aiCostRadar, ai: true, note: `${num(radar)} users × ${ai(usdUnit, sim.aiCostPerRadarUser)} per user` },
    {
      label: "Firestore writes",
      value: sim.fsWritesCost,
      note: `Fan-out: ${((NEW_JOBS_PER_DAY * (activeFree + search + radar) * 30) / 1e6).toFixed(1)}M writes per month`,
      warn: sim.fsWritesCost > 30,
    },
    { label: "Firestore reads", value: sim.fsReadsCost, note: "Scans, digests and salary aggregation" },
    { label: "Cloud Functions", value: sim.cfCost, note: "2M per month free, $0.40 per million after" },
    { label: "Cloud Storage", value: sim.storageCost, note: `About ${((total * 15) / 1024).toFixed(1)} GB of PDFs (5 GB free)` },
    { label: "Mailgun email", value: sim.emailCost, note: "5K per month free, $0.50 per thousand after" },
  ];
  type CostRow = (typeof costRows)[number];

  const opCost = (key: OpKey) => tc(OPS[key].input, OPS[key].output, tokenPrice);
  type OpRow = (typeof BREAKDOWN)[number];
  const tierCell = (tier: "free" | "search" | "radar") => (r: OpRow) =>
    r[tier] > 0 ? (
      <span className="flex flex-col items-end">
        <span className={priceKnown ? undefined : "text-subtle-foreground"}>{ai(usdUnit, r[tier] * opCost(r.key))}</span>
        <span className="text-caption text-muted-foreground">{num(r[tier])} {r[tier] === 1 ? "call" : "calls"}</span>
      </span>
    ) : (
      <span className="text-subtle-foreground">—</span>
    );

  const mrrForBar = Math.max(sim.mrr, sim.totalCost, 0.01);

  return (
    <div>
      <SectionHeader
        title="Cost Simulator"
        subtitle="Planning scenario, not live results. Assumptions reset when you leave the page."
        action={
          <Segmented
            label="Scenario"
            value={scenario}
            onChange={applyScenario}
            options={[
              { value: "bad", label: SCENARIOS.bad.label },
              { value: "normal", label: SCENARIOS.normal.label },
              { value: "good", label: SCENARIOS.good.label },
              { value: "custom", label: "Custom" },
            ]}
          />
        }
      />

      {/* The three numbers that decide, with MRR against costs (admin-16). */}
      <Panel className="mb-6">
        {!priceKnown && (
          <Callout tone="warning" className="mb-4" title="AI price unknown">
            <span className="font-mono">{resolvedModelId}</span> is not in MODEL_COST_MAP (@winche/shared), so AI costs, the
            total and the margin are left out instead of borrowing another model&apos;s price. Add its price to the map.
          </Callout>
        )}
        <MetricGrid cols={3}>
          <Metric size="lg" label="Projected list MRR" value={usd(sim.mrr)} className="border-0 p-0" />
          <Metric
            size="lg"
            label="Projected cost per month"
            value={priceKnown ? usd(sim.totalCost) : "—"}
            sub={priceKnown ? undefined : `Infrastructure ${usd(sim.infraTotal)} + AI price unknown`}
            tone={priceKnown && losing ? "bad" : "default"}
            className="border-0 p-0"
          />
          <Metric
            size="lg"
            label="Scenario margin"
            value={priceKnown && sim.mrr > 0 ? pct(sim.grossMarginPct) : "—"}
            sub={priceKnown ? usd(sim.grossProfit) : undefined}
            tone={priceKnown && sim.mrr > 0 && sim.grossMarginPct < 0 ? "bad" : priceKnown && sim.grossMarginPct >= 50 ? "good" : "default"}
            className="border-0 p-0"
          />
        </MetricGrid>
        <div className="mt-5 space-y-2" aria-hidden="true">
          {[
            { label: "MRR", parts: [{ value: sim.mrr, color: "var(--chart-1)" }] },
            {
              label: "Cost",
              parts: [
                ...(priceKnown ? [{ value: sim.aiTotal, color: "var(--chart-2)" }] : []),
                { value: sim.infraTotal, color: "var(--chart-6)" },
              ],
            },
          ].map((row) => (
            <div key={row.label} className="flex items-center gap-3">
              <span className="w-10 text-caption text-muted-foreground">{row.label}</span>
              <div className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                {row.parts.map((p, i) => (
                  <div key={i} className="h-full" style={{ width: `${(p.value / mrrForBar) * 100}%`, backgroundColor: p.color }} />
                ))}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-x-4 gap-y-1 pl-[52px] text-caption text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-sm bg-[color:var(--chart-2)]" /> AI {ai(usd, sim.aiTotal)}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-sm bg-[color:var(--chart-6)]" /> Infrastructure {usd(sim.infraTotal)}
            </span>
          </div>
        </div>
      </Panel>

      <div className="mb-6 grid grid-cols-1 gap-6 @3xl:grid-cols-2">
        <Panel>
          <h2 className="mb-4 text-sm font-medium">User mix</h2>
          <Slider id="sim-total" label="Total users" value={total} display={num(total)} min={0} max={5000} step={50} minLabel="0" maxLabel="5,000" onChange={custom(setTotal)} />
          <Slider
            id="sim-search"
            label={`PRO Search ($${PRICE_SEARCH}/mo) — ${num(search)} users`}
            value={searchPct}
            display={pct(searchPct)}
            min={0}
            max={30}
            step={0.5}
            minLabel="0%"
            maxLabel="30%"
            onChange={custom(setSearchPct)}
          />
          <Slider
            id="sim-radar"
            label={`PRO Radar ($${PRICE_RADAR}/mo) — ${num(radar)} users`}
            value={radarPct}
            display={pct(radarPct)}
            min={0}
            max={30}
            step={0.5}
            minLabel="0%"
            maxLabel="30%"
            onChange={custom(setRadarPct)}
          />
          <div className="mt-2 border-t border-border pt-4">
            <Slider
              id="sim-retention"
              label={`Monthly retention — ${num(sim.activeUsers - search - radar)} active free users`}
              value={retentionPct}
              display={pct(retentionPct, 0)}
              min={5}
              max={80}
              step={5}
              minLabel="5%"
              maxLabel="80%"
              onChange={custom(setRetentionPct)}
            />
            <p className="text-caption text-muted-foreground">
              AI at retail: <span className="font-mono">{resolvedModelId}</span>{" "}
              {knownPrice
                ? `($${(knownPrice.input * 1_000_000).toFixed(2)} / $${(knownPrice.output * 1_000_000).toFixed(2)} per 1M tokens)`
                : "(price unknown)"}
            </p>
          </div>
        </Panel>

        <Panel>
          <h2 className="mb-3 text-sm font-medium">Other results</h2>
          <dl className="divide-y divide-border text-label">
            {[
              ["Active users", num(sim.activeUsers)],
              ["PRO Search", `${num(search)} (${pct(searchPct)})`],
              ["PRO Radar", `${num(radar)} (${pct(radarPct)})`],
              ["Conversion", pct(sim.conversionPct)],
              ["Saved by skipping inactive users", ai(usd, sim.inactiveAiSaved)],
              ["AI cost per Search user", ai(usdUnit, sim.aiCostPerSearchUser)],
              ["AI cost per Radar user", ai(usdUnit, sim.aiCostPerRadarUser)],
              ["AI cost per free user", ai(usdUnit, sim.aiCostPerFreeUser)],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-3 py-2">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>

      <Block title="Monthly cost by component">
        <DataTable
          caption="Monthly cost by component"
          rows={costRows}
          rowKey={(r) => r.label}
          columns={[
            {
              key: "component",
              header: "Component",
              cell: (r: CostRow) => (
                <span className="flex flex-col">
                  <span className={r.warn ? "text-warning-fg" : undefined}>{r.label}</span>
                  {/* Notes stay visible on a phone as a second line (admin-11). */}
                  <span className="text-caption text-muted-foreground">{r.note}</span>
                </span>
              ),
              footer: "Total",
            },
            {
              key: "value",
              header: "Per month",
              align: "right",
              cell: (r: CostRow) =>
                r.value == null ? (
                  <span className="text-subtle-foreground">—</span>
                ) : r.ai && !priceKnown ? (
                  <span className="text-subtle-foreground">price unknown</span>
                ) : (
                  usd(r.value)
                ),
              footer: priceKnown ? <span className={losing ? "text-danger-fg" : undefined}>{usd(sim.totalCost)}</span> : "—",
            },
          ]}
        />
      </Block>

      <Block title="AI cost per user per month" hint="Each operation at retail price for one user of each tier, with the monthly call count.">
        <DataTable
          caption="AI cost per user and operation"
          rows={BREAKDOWN}
          rowKey={(r) => r.op}
          columns={[
            { key: "op", header: "Operation", cell: (r) => <span className="font-mono">{r.op}</span>, footer: "Total" },
            { key: "free", header: "Free (active)", align: "right", cell: tierCell("free"), footer: ai(usdUnit, sim.aiCostPerFreeUser) },
            { key: "search", header: "PRO Search", align: "right", cell: tierCell("search"), footer: ai(usdUnit, sim.aiCostPerSearchUser) },
            { key: "radar", header: "PRO Radar", align: "right", cell: tierCell("radar"), footer: ai(usdUnit, sim.aiCostPerRadarUser) },
          ]}
        />
      </Block>

      <Block title="OpenRouter free tier">
        <MetricGrid cols={4} className="mb-3">
          <Metric label="Peak requests per minute" value={num(Math.round(sim.peakRpm))} sub={`Free limit ${OR_FREE.rpm}`} tone={sim.orFreeOk ? "default" : "bad"} />
          <Metric label="Average requests per day" value={num(Math.round(sim.dailyReqs))} sub={`Free limit ${OR_FREE.rpd}`} tone={sim.orRpdOk ? "default" : "bad"} />
          <Metric label="Free tier viable" value={orOk ? "Yes" : "No"} tone={orOk ? "good" : "bad"} />
          <Metric label="AI cost per month" value={priceKnown ? usd(sim.aiTotal) : "—"} />
        </MetricGrid>
        {orOk ? (
          <Callout tone="success">The free tier covers this volume.</Callout>
        ) : (
          <Callout tone="danger" title="The free tier hits rate limits at this volume">
            {priceKnown
              ? `Add paid OpenRouter credits before launch: about ${usd(sim.aiTotal)} per month, ${usd(sim.aiTotal * 3)} for a 3-month buffer.`
              : "Add paid OpenRouter credits before launch (the AI price of this model is unknown)."}
          </Callout>
        )}
      </Block>
    </div>
  );
}
